export type AssetId = string;
export type RootId = string;
export type MediaKind =
  | "image"
  | "raw"
  | "video"
  | "audio"
  | "pdf"
  | "photoshop"
  | "illustrator"
  | "indesign"
  | "document"
  | "other";

export type ColorLabel = "none" | "red" | "orange" | "yellow" | "green" | "blue" | "purple";

export interface AssetMetadata {
  title?: string;
  description?: string;
  creator?: string;
  copyright?: string;
  usageTerms?: string;
  keywords: string[];
  cameraMake?: string;
  cameraModel?: string;
  lens?: string;
  captureDate?: string;
  width?: number;
  height?: number;
  duration?: number;
  iso?: number;
  aperture?: number;
  shutterSpeed?: string;
  focalLength?: number;
  orientation?: number;
  raw?: Record<string, unknown>;
}

export interface RawRecipe {
  schemaVersion: 1;
  engineVersion: string;
  exposure: number;
  contrast: number;
  highlights: number;
  shadows: number;
  saturation: number;
  temperature: number;
  tint: number;
  rotation: 0 | 90 | 180 | 270;
  crop?: { x: number; y: number; width: number; height: number };
  sharpening: { amount: number; radius: number };
  denoise: { luminance: number; chroma: number };
}

export interface AssetRecord {
  id: AssetId;
  rootId: RootId;
  relativePath: string;
  parentPath: string;
  filename: string;
  extension: string;
  mimeType: string;
  kind: MediaKind;
  size: number;
  lastModified: number;
  fingerprint: string;
  rating: 0 | 1 | 2 | 3 | 4 | 5;
  label: ColorLabel;
  metadata: AssetMetadata;
  adjustment?: RawRecipe;
  createdAt: number;
  updatedAt: number;
  missing?: boolean;
}

export interface RootRecord {
  id: RootId;
  name: string;
  handleKey: string;
  addedAt: number;
  lastScanAt?: number;
  permission: "granted" | "prompt" | "denied" | "unknown";
}

export type QueryField =
  | "filename"
  | "relativePath"
  | "kind"
  | "extension"
  | "rating"
  | "label"
  | "keywords"
  | "cameraModel"
  | "cameraMake"
  | "creator"
  | "copyright"
  | "captureDate"
  | "size";

export type QueryComparison =
  | "contains"
  | "equals"
  | "notEquals"
  | "startsWith"
  | "endsWith"
  | "greaterThan"
  | "greaterThanOrEqual"
  | "lessThan"
  | "lessThanOrEqual"
  | "before"
  | "after";

export interface QueryRule {
  field: QueryField;
  comparison: QueryComparison;
  value: string | number;
}

export interface QueryTree {
  version: 1;
  operator: "and" | "or";
  rules: QueryRule[];
}

export interface SavedSearch {
  id: string;
  name: string;
  query: QueryTree;
  createdAt: number;
  updatedAt: number;
}

export interface Collection {
  id: string;
  name: string;
  kind: "manual" | "smart";
  assetIds: AssetId[];
  query?: QueryTree;
  createdAt: number;
  updatedAt: number;
}

export type JobStatus =
  | "planned"
  | "preflighted"
  | "running"
  | "verifying"
  | "completed"
  | "failed"
  | "cancelled"
  | "needs-user-action";

export interface JobRecord<TPayload = unknown> {
  id: string;
  type: string;
  status: JobStatus;
  progress: number;
  completedItems: number;
  totalItems: number;
  message: string;
  payload: TPayload;
  error?: string;
  createdAt: number;
  updatedAt: number;
}

export interface ExportPreset {
  id: string;
  name: string;
  format: "image/jpeg" | "image/png" | "image/webp";
  quality: number;
  maxWidth: number;
  maxHeight: number;
  filenameTemplate: string;
  metadataPolicy: "preserve" | "copyright-only" | "strip";
  overwrite: "skip" | "replace" | "suffix";
}

const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "webp", "avif", "bmp", "tif", "tiff", "heic", "heif"]);
const RAW_EXTENSIONS = new Set(["cr2", "cr3", "nef", "nrw", "arw", "srf", "sr2", "raf", "orf", "rw2", "pef", "dng", "3fr", "erf", "kdc", "mos", "mrw", "x3f"]);
const VIDEO_EXTENSIONS = new Set(["mp4", "mov", "m4v", "webm", "mkv", "avi", "mts", "m2ts"]);
const AUDIO_EXTENSIONS = new Set(["mp3", "wav", "flac", "m4a", "aac", "ogg"]);

export function extensionOf(filename: string): string {
  const index = filename.lastIndexOf(".");
  return index > 0 ? filename.slice(index + 1).toLowerCase() : "";
}

export function detectMediaKind(filename: string, mimeType = ""): MediaKind {
  const ext = extensionOf(filename);
  if (RAW_EXTENSIONS.has(ext)) return "raw";
  if (IMAGE_EXTENSIONS.has(ext) || mimeType.startsWith("image/")) return "image";
  if (VIDEO_EXTENSIONS.has(ext) || mimeType.startsWith("video/")) return "video";
  if (AUDIO_EXTENSIONS.has(ext) || mimeType.startsWith("audio/")) return "audio";
  if (ext === "pdf" || mimeType === "application/pdf") return "pdf";
  if (ext === "psd" || ext === "psb") return "photoshop";
  if (ext === "ai" || ext === "eps") return "illustrator";
  if (ext === "indd" || ext === "idml") return "indesign";
  if (["txt", "md", "rtf", "doc", "docx", "odt"].includes(ext)) return "document";
  return "other";
}

export function normalizeRelativePath(value: string): string {
  const parts = value.replaceAll("\\", "/").split("/").filter((part) => part && part !== ".");
  if (parts.some((part) => part === "..")) throw new Error("Path traversal is not allowed");
  return parts.join("/");
}

export function parentPathOf(relativePath: string): string {
  const normalized = normalizeRelativePath(relativePath);
  const index = normalized.lastIndexOf("/");
  return index === -1 ? "" : normalized.slice(0, index);
}

export function makeAssetId(rootId: string, relativePath: string): string {
  const input = `${rootId}:${normalizeRelativePath(relativePath).toLocaleLowerCase("en-US")}`;
  let hash = 2166136261;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `asset_${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

export const DEFAULT_RAW_RECIPE: RawRecipe = {
  schemaVersion: 1,
  engineVersion: "focalfolio-canvas-1",
  exposure: 0,
  contrast: 0,
  highlights: 0,
  shadows: 0,
  saturation: 0,
  temperature: 0,
  tint: 0,
  rotation: 0,
  sharpening: { amount: 0, radius: 0.8 },
  denoise: { luminance: 0, chroma: 0 }
};

export const DEFAULT_EXPORT_PRESET: ExportPreset = {
  id: "web-jpeg",
  name: "Web JPEG",
  format: "image/jpeg",
  quality: 0.88,
  maxWidth: 2400,
  maxHeight: 2400,
  filenameTemplate: "{originalName}_export",
  metadataPolicy: "copyright-only",
  overwrite: "suffix"
};
